import React from 'react';
import { Formik, FormikProps, Form } from 'formik';
import { Grid, TextField, Button, makeStyles, FormControl } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import * as Yup from 'yup';
import Select from '../../../../Components/Select';
import useHandleAuthError from '../../../../utils/hooks/useHandleAuthError';
import AutoSuggest from '../../../Bookings/BookingForm/AutoSuggest';
import { HotelInput, Hotel, Partner } from './types';
import { useRoomTypesQuery } from '../../graphql/roomTypes.generated';
import { useHotelsQuery } from '../../graphql/hotels.generated';

interface Props {
  initialValues?: HotelInput;
  submitLabel?: string;
  onSubmit: (values: HotelInput) => void;
  onSuccess?: () => void;
  partnerOptions: Array<{ label: string; value: Partner }>;
}

const bookingSchema = Yup.object().shape({
  hotel: Yup.string().required('Required')
});

const defaultValues: HotelInput = {
  checkIn: new Date(),
  checkOut: new Date(),
  adults: 0,
  children: 0,
  infants: 0,
  roomType: '',
  hotel: null,
  partner: null,
  childrenAge: '',
  notes: '',
  board: '',
  total: 0,
  view: '',
  extra: ''
};

const useStyles = makeStyles((theme) => ({
  input: {
    width: '100%'
  },
  form: {
    marginTop: theme.spacing(2)
  },
  hotel: {
    display: 'flex',
    alignItems: 'center'
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    width: '50%',
    padding: theme.spacing(2, 4, 3)
  }
}));

const HotelForm = ({
  initialValues = defaultValues,
  submitLabel = 'Add booking',
  onSubmit,
  onSuccess,
  partnerOptions
}: Props) => {
  console.log({ initialValues });
  const { form, input, hotel } = useStyles();
  const onError = useHandleAuthError();
  const { data: roomTypesData } = useRoomTypesQuery();
  const roomTypes = roomTypesData?.roomTypes || [];

  const { data } = useHotelsQuery({ onError });

  const suggestions: Array<{ label: string; value: Hotel }> =
    data?.hotels?.map((hotel) => ({ label: hotel?.name || '', value: { id: hotel?.id, name: hotel?.name } })) || [];

  const handleSubmit = (values: HotelInput) => {
    const { id, ...rest } = values;
    onSubmit(rest);
    onSuccess?.();
  };

  return (
    <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={handleSubmit} validationSchema={bookingSchema}>
      {({ errors, touched, handleChange, handleBlur, values, setFieldValue }: FormikProps<HotelInput>) => (
        <Form className={form}>
          <Grid container spacing={4}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <Grid className={hotel} item xs={12}>
                <AutoSuggest
                  name="hotel"
                  onChange={(value) => setFieldValue('hotel', value)}
                  suggestions={suggestions}
                  error={!!errors.hotel && !!touched.hotel}
                  placeholder="Search for hotel"
                  value={values.hotel}
                  label="Hotel"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <KeyboardDatePicker
                  format="dd/MM/yyyy"
                  onFocus={() => {}}
                  margin="normal"
                  id="date-picker-inline"
                  label="Check In"
                  KeyboardButtonProps={{
                    'aria-label': 'change date'
                  }}
                  name="checkIn"
                  inputVariant="outlined"
                  value={values.checkIn}
                  onChange={(date) => setFieldValue('checkIn', date)}
                  onBlur={handleBlur}
                  error={!!errors.checkIn && !!touched.checkIn}
                  helperText={!!errors.checkIn && !!touched.checkIn && errors.checkIn}
                  className={input}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <KeyboardDatePicker
                  format="dd/MM/yyyy"
                  margin="normal"
                  onFocus={() => {}}
                  id="date-picker-inline"
                  label="Check Out"
                  inputVariant="outlined"
                  KeyboardButtonProps={{
                    'aria-label': 'change date'
                  }}
                  minDate={values.checkIn}
                  name="checkOut"
                  value={values.checkOut}
                  onChange={(date) => setFieldValue('checkOut', date)}
                  onBlur={handleBlur}
                  error={!!errors.checkOut && !!touched.checkOut}
                  helperText={!!errors.checkOut && !!touched.checkOut && errors.checkOut}
                  className={input}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  id="outlined-number"
                  label="Adults"
                  name="adults"
                  InputLabelProps={{
                    shrink: true
                  }}
                  type="number"
                  onWheel={(e) => {
                    // Prevent the input value change
                    (e.target as HTMLInputElement).blur();
                  }}
                  value={values.adults}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.adults && !!touched.adults}
                  helperText={!!errors.adults && !!touched.adults && errors.adults}
                  margin="normal"
                  className={input}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  id="outlined-name"
                  label="Children"
                  name="children"
                  type="number"
                  onWheel={(e) => {
                    // Prevent the input value change
                    (e.target as HTMLInputElement).blur();
                  }}
                  value={values.children}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.children && !!touched.children}
                  helperText={!!errors.children && !!touched.children && errors.children}
                  margin="normal"
                  className={input}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  id="outlined-name"
                  label="Infants"
                  name="infants"
                  type="number"
                  onWheel={(e) => {
                    // Prevent the input value change
                    (e.target as HTMLInputElement).blur();
                  }}
                  value={values.infants}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.infants && !!touched.infants}
                  helperText={!!errors.infants && !!touched.infants && errors.infants}
                  margin="normal"
                  className={input}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                <TextField
                  id="outlined-name"
                  label="Children age"
                  name="childrenAge"
                  value={values.childrenAge}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.childrenAge && !!touched.childrenAge}
                  helperText={!!errors.childrenAge && !!touched.childrenAge && errors.childrenAge}
                  margin="normal"
                  variant="outlined"
                  className={input}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl className={input}>
                  <Select
                    value={values.roomType}
                    label="Room type"
                    onBlur={handleBlur}
                    onChange={(event) => setFieldValue('roomType', event.target.value)}
                    options={roomTypes.map((roomType) => ({ value: roomType || '', name: roomType || '' }))}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl className={input}>
                  <Select
                    value={values.view}
                    label="View"
                    onBlur={handleBlur}
                    onChange={(event) => setFieldValue('view', event.target.value)}
                    options={[
                      { value: 'garden', name: 'Garden' },
                      { value: 'sea', name: 'Sea' },
                      { value: 'side sea', name: 'Side sea' },
                      { value: 'mountain', name: 'Mountain' },
                      { value: 'pool', name: 'Pool' }
                    ]}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl className={input}>
                  <Select
                    name="board"
                    onBlur={handleBlur}
                    label="Board"
                    value={values.board}
                    onChange={(event) => setFieldValue('board', event.target.value)}
                    options={[
                      { value: 'room only', name: 'Room Only' },
                      { value: 'full board', name: 'Full board' },
                      { value: 'half board', name: 'Half board' },
                      { value: 'full board + drinks', name: 'Full board + drinks' },
                      { value: 'half board + drinks', name: 'Half board + drinks' },
                      { value: 'bed + breakfast', name: 'Bed + breakfast' },
                      { value: 'smart all inclusive', name: 'Smart all inclusive' },
                      { value: 'all inclusive', name: 'All inclusive' },
                      { value: 'ultra all inclusive', name: 'Ultra all inclusive' },
                      { value: 'premium all inclusive', name: 'Premium all inclusive' },
                      { value: 'soft all inclusive', name: 'Soft all inclusive' },
                      { value: 'light all inclusive', name: 'Light all inclusive' }
                    ]}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl className={input}>
                  <Select
                    onBlur={handleBlur}
                    value={values.extra}
                    onChange={(event) => setFieldValue('extra', event.target.value)}
                    label="Extra"
                    options={[
                      { name: 'Extra Bed', value: 'bed' },
                      { name: 'Extra cot', value: 'cot' },
                      { name: 'Bunk Bed', value: 'bunk bed' },
                      { name: 'Cot + Bed', value: 'cot + bed' }
                    ]}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={12}>
                <AutoSuggest
                  label="Partner agency"
                  error={!!errors.partner && !!touched.partner}
                  placeholder="Search for partner"
                  value={values.partner}
                  name="partner"
                  onChange={(value) => setFieldValue('partner', value)}
                  suggestions={partnerOptions}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="standard-textarea"
                  label="Net total"
                  value={values.total}
                  name="total"
                  type="number"
                  onWheel={(e) => {
                    // Prevent the input value change
                    (e.target as HTMLInputElement).blur();
                  }}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className={input}
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="standard-textarea"
                  label="Notes"
                  value={values.notes}
                  name="notes"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={input}
                  multiline
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
            </MuiPickersUtilsProvider>
            <Grid item xs={12}>
              <Button variant="contained" type="submit" color="primary">
                {submitLabel}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default HotelForm;
